import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from '@/store'
import {
  RoleModelFinderState,
  UserType,
  CareerQuestionAnswer,
  ValueAnswer,
  ValueAnswerItem,
  RoleModelFinderDetailRecord,
  RoleModelFinderDetail,
  InterviewQuestions,
} from '@/types/roleModelFinder'
import { RoleModelFinderCompanyResponse } from '@/lib/api/types/api'
import { ProfileData, InterestedOccupation } from '@/types/profile'
import interviewQuestions from '@/constants/interviewQuestions.json'
import { MembersService } from '@/lib/api/services/members'
import { CompaniesService } from '@/lib/api/services/companies'
import { SESSION_STORAGE_KEYS } from '@/constants/storageKeys'
import { ApiError } from '@/utility/apiClient'

const saveToSessionStorage = (state: RoleModelFinderState): void => {
  if (typeof window !== 'undefined') {
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEYS.ROLE_MODEL_FINDER_STATE, JSON.stringify(state))
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to save to sessionStorage:', error)
      }
    }
  }
}

const loadFromSessionStorage = (): Partial<RoleModelFinderState> | null => {
  if (typeof window !== 'undefined') {
    try {
      const savedState = sessionStorage.getItem(SESSION_STORAGE_KEYS.ROLE_MODEL_FINDER_STATE)
      if (savedState) {
        return JSON.parse(savedState)
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to load from sessionStorage:', error)
      }
    }
  }
  return null
}

export const hasCompaniesInStorage = (): boolean => {
  if (typeof window !== 'undefined') {
    try {
      const savedState = sessionStorage.getItem(SESSION_STORAGE_KEYS.ROLE_MODEL_FINDER_STATE)
      if (savedState) {
        const parsed = JSON.parse(savedState)
        return parsed?.companies && Array.isArray(parsed.companies) && parsed.companies.length > 0
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to check companies in sessionStorage:', error)
      }
    }
  }
  return false
}

const initialState: RoleModelFinderState = {
  currentStep: 1,
  userType: 'worker',
  careerQuestionAnswers: [],
  valueAnswers: {},
  roleModel: {} as RoleModelFinderDetailRecord,
  isRequestInProgress: false,
  companies: [] as RoleModelFinderCompanyResponse[],
  isLoadingCompanies: false,
  selectedCompanies: [],
  interested_occupations: [] as InterestedOccupation[],
}

export const fetchRecommendations = createAsyncThunk(
  'roleModelFinder/fetchRecommendations',
  async (memberId: string, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState
      const roleModelFinderState = state.roleModelFinder

      if (!memberId) {
        throw new Error('Member ID is required')
      }

      const userInputText = formatUserInputText(roleModelFinderState)

      // valueAnswersからタグIDの配列を生成
      const valueTagIds: number[] = []
      const careerTagIds: number[] = []
      Object.values(roleModelFinderState.valueAnswers).forEach((answerItem: ValueAnswerItem) => {
        if (answerItem.valueTagId) {
          valueTagIds.push(answerItem.valueTagId)
        }
        if (answerItem.careerTagId) {
          careerTagIds.push(answerItem.careerTagId)
        }
      })

      const requestBody = {
        user_input_text: userInputText,
        company_ids: roleModelFinderState.selectedCompanies.join(','),
        value_tag_ids: valueTagIds,
        career_tag_ids: careerTagIds,
      }

      const response = await MembersService.createRoleModelFinder(memberId, requestBody)

      // レスポンスデータを変換
      const transformedResults = {
        id: response.id,
        createdAt: response.createdAt,
        details: response.details.map((model: RoleModelFinderDetail) => ({
          id: model.id,
          name: model.name,
          imageUrl: model.imageUrl || '/images/default_user.png',
          deptAndTitle: model.deptAndTitle,
          raireki: model.raireki,
          occupation: model.occupation,
          recommendedReason: model.recommendedReason,
          company: {
            id: model.company?.id,
            name: model.company?.name,
            categories: model.company?.categories,
          },
          story: {
            id: model.story.id,
            title: model.story.title,
            eyecatchImageUrl: model.story.eyecatchImageUrl,
            summary: model.story.summary,
            path: model.story.path,
          },
        })),
      }

      return transformedResults
    } catch (error: unknown) {
      if (error instanceof ApiError) {
        return rejectWithValue({
          message: error.message || 'An unknown error occurred',
          status: error.status || 500,
        })
      }
      const message = error instanceof Error ? error.message : 'An unknown error occurred'
      return rejectWithValue({
        message,
        status: 500,
      })
    }
  },
)

export const fetchCompanies = createAsyncThunk<
  RoleModelFinderCompanyResponse[],
  void,
  { rejectValue: string }
>('roleModelFinder/fetchCompanies', async (_, { rejectWithValue }) => {
  try {
    const response = await CompaniesService.getRoleModelFinderCompanies()
    return response || []
  } catch (error) {
    return rejectWithValue('企業データの取得に失敗しました')
  }
})

export const fetchMemberJob = createAsyncThunk(
  'roleModelFinder/fetchMemberJob',
  async (_, { rejectWithValue }) => {
    try {
      const response = await MembersService.getCurrentProfile()
      return response as ProfileData
    } catch (error: unknown) {
      if (error instanceof ApiError) {
        return rejectWithValue({
          message: error.message || 'An unknown error occurred',
          status: error.status || 500,
        })
      }
      const message = error instanceof Error ? error.message : 'An unknown error occurred'
      return rejectWithValue({
        message,
        status: 500,
      })
    }
  },
)

export const fetchCareerInterview = createAsyncThunk(
  'roleModelFinder/fetchCareerInterview',
  async (memberId: string, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState
      const roleModelFinderState = state.roleModelFinder

      if (!memberId) {
        throw new Error('Member ID is required')
      }

      const response = await MembersService.getCareerInterview(memberId, {
        user_type: roleModelFinderState.userType,
      })

      return response
    } catch (error: unknown) {
      if (error instanceof ApiError) {
        return rejectWithValue({
          message: error.message || 'An unknown error occurred',
          status: error.status || 500,
        })
      }
      const message = error instanceof Error ? error.message : 'An unknown error occurred'
      return rejectWithValue({
        message,
        status: 500,
      })
    }
  },
)

const formatUserInputText = (state: RoleModelFinderState): string => {
  const careerQuestionAnswers = state.careerQuestionAnswers
    .map((q: CareerQuestionAnswer) => `Q) ${q.questionTitle} A) ${q.answer}`)
    .join(' ')

  return careerQuestionAnswers.trim()
}

const roleModelFinderSlice = createSlice({
  name: 'roleModelFinder',
  initialState,
  reducers: {
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload
      saveToSessionStorage(state)
    },
    setUserType: (state, action: PayloadAction<UserType>) => {
      if (action.payload !== null) {
        state.userType = action.payload
        saveToSessionStorage(state)
      }
    },
    setCareerQuestionAnswers: (state, action: PayloadAction<CareerQuestionAnswer[]>) => {
      state.careerQuestionAnswers = action.payload
      saveToSessionStorage(state)
    },
    setValueAnswers: (state, action: PayloadAction<ValueAnswer>) => {
      state.valueAnswers = action.payload
      saveToSessionStorage(state)
    },
    setRoleModel: (state, action: PayloadAction<RoleModelFinderDetailRecord>) => {
      state.roleModel = action.payload
      saveToSessionStorage(state)
    },
    setSelectedCompanies: (state, action: PayloadAction<number[]>) => {
      state.selectedCompanies = action.payload
      saveToSessionStorage(state)
    },
    addSelectedCompany: (state, action: PayloadAction<number>) => {
      if (!state.selectedCompanies.includes(action.payload)) {
        state.selectedCompanies.push(action.payload)
        saveToSessionStorage(state)
      }
    },
    removeSelectedCompany: (state, action: PayloadAction<number>) => {
      state.selectedCompanies = state.selectedCompanies.filter((id) => id !== action.payload)
      saveToSessionStorage(state)
    },
    clearSelectedCompanies: (state) => {
      state.selectedCompanies = []
      saveToSessionStorage(state)
    },
    loadFromStorage: (state) => {
      const savedState = loadFromSessionStorage()
      if (savedState) {
        return { ...state, ...savedState, isRequestInProgress: false, isLoadingCompanies: false }
      }
    },
    clearAllData: (state) => {
      // companiesだけは残しておく
      const companies = state.companies

      if (typeof window !== 'undefined') {
        try {
          sessionStorage.removeItem(SESSION_STORAGE_KEYS.ROLE_MODEL_FINDER_STATE)
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Failed to clear sessionStorage:', error)
          }
        }
      }

      const newState = { ...initialState, companies: companies }
      saveToSessionStorage(newState)
      return newState
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch recommendations
      .addCase(fetchRecommendations.pending, (state) => {
        state.isRequestInProgress = true
      })
      .addCase(fetchRecommendations.fulfilled, (state, action) => {
        state.roleModel = action.payload
        state.isRequestInProgress = false
        saveToSessionStorage(state)
      })
      .addCase(fetchRecommendations.rejected, (state) => {
        state.isRequestInProgress = false
      })
      // Fetch companies
      .addCase(fetchCompanies.pending, (state) => {
        state.isLoadingCompanies = true
      })
      .addCase(fetchCompanies.fulfilled, (state, action) => {
        state.companies = action.payload || []
        state.isLoadingCompanies = false
        saveToSessionStorage(state)
      })
      .addCase(fetchCompanies.rejected, (state) => {
        // エラー時は空配列を設定してリトライを防ぐ
        state.companies = []
        state.isLoadingCompanies = false
      })
      .addCase(fetchMemberJob.fulfilled, (state, action) => {
        if (action.payload.job === 1 || action.payload.job === 3) {
          state.userType = 'student'
        } else {
          state.userType = 'worker'
        }

        if (action.payload.interestedOccupations) {
          state.interested_occupations = action.payload.interestedOccupations
        }
        saveToSessionStorage(state)
      })
      .addCase(fetchCareerInterview.fulfilled, (state, action) => {
        state.careerQuestionAnswers = action.payload
        saveToSessionStorage(state)
      })
  },
})

export const {
  setCurrentStep,
  setUserType,
  setCareerQuestionAnswers,
  setValueAnswers,
  setRoleModel,
  setSelectedCompanies,
  addSelectedCompany,
  removeSelectedCompany,
  clearSelectedCompanies,
  loadFromStorage,
  clearAllData,
} = roleModelFinderSlice.actions

// インタビューの進捗管理用セレクター
export const selectValueAnswersCount = (state: RootState): number =>
  Object.keys(state.roleModelFinder.valueAnswers).length

export const selectValueQuestionsTotal = (state: RootState): number => {
  const questions: InterviewQuestions = interviewQuestions
  const userType = state.roleModelFinder.userType
  return userType ? questions.valueInterview[userType]?.length || 0 : 0
}

export const selectIsAllValueAnswered = (state: RootState): boolean =>
  selectValueAnswersCount(state) === selectValueQuestionsTotal(state)

export default roleModelFinderSlice.reducer
